/**
 * ホームのカード「支払予定」（#233 3a、docs/93 §15.5・§15.10 ②-1／②-2）。入金待ちのカードと同じ作り（.awaitpay）で入金待ちの下。
 * 確定した仕入のうち 支払期日 7 日以内と期限超過。件数・合計・うち期限超過、上から5行（期限超過が上・日数つき）。
 * 行を押すと仕入の画面。行の右の「支払登録」（#233 3c・PC だけ）でその仕入の支払の窓。残が 0 になった仕入はここから外れる。
 * sales の会社だけ（API が purchasesDue を返したときだけ）。0件でも枠を出す
 */
import { HOME_MSG, PURCHASE_MSG, VENDOR_PAYMENT_MSG, overdueText, type HomeSummary } from "@genbaryoku/contract";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Icon } from "../../components/Shell.jsx";
import { Help } from "../../components/ui.jsx";
import { md, yen } from "../../lib/num.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { VendorPaymentDialog } from "./VendorPayments.jsx";

export function HomePurchasesDue({ home }: { home: HomeSummary }) {
  const a = home.purchasesDue;
  const nav = useNavigate();
  const pc = usePcOnly();
  const [paying, setPaying] = useState<{ purchaseId: string; vendorId: string; vendorName: string } | null>(null);
  if (!a) return null;
  const payable = home.months.thisMonth.payable?.count ?? 0;
  return (
    <section className={"card awaitpay" + (a.count ? "" : " zero")} data-testid="home-purchases-due">
      <div className="aphead">
        <h3 className="hc-title">
          <span className="hc-mark" aria-hidden="true">
            <Icon k="card" />
          </span>
          <span className="hc-tt">{PURCHASE_MSG.homeTitle}</span>
          <Help testId="help-purchases-due" text={PURCHASE_MSG.homeHelp} />
        </h3>
        <span className="sp" />
        <span className="apsum" data-testid={`card-${PURCHASE_MSG.homeTitle}`}>
          <b className="num">{a.count}</b>件
          {a.count ? (
            <>
              <span className="apgap">{HOME_MSG.total}</span>
              <b className="num">{yen(a.total)}</b>円
              {a.overdueCount ? (
                <span className="shu apover">
                  {HOME_MSG.overdueSum} <b className="num">{a.overdueCount}</b> 件 <span className="num">{yen(a.overdueTotal)}</span> 円
                </span>
              ) : null}
            </>
          ) : null}
        </span>
      </div>
      {a.rows.length ? (
        <div className="aprows">
          {a.rows.map((r) => (
            <div key={r.purchaseId} className="aprow" data-testid="purchase-due-row" onClick={() => nav(`/purchases/${r.purchaseId}`)}>
              {r.state === "overdue" ? (
                <span className="pill shu apdue" data-testid="purchase-due-overdue">
                  <Icon k="cal" />
                  {overdueText(r.overdueDays)}
                </span>
              ) : (
                <span className="pill quiet apdue num">
                  <Icon k="cal" />
                  {r.dueDate ? HOME_MSG.due(md(r.dueDate)) : HOME_MSG.noDue}
                </span>
              )}
              <span className="apname">
                <Link to={`/purchases/${r.purchaseId}`} onClick={(e) => e.stopPropagation()} title={r.vendorName}>
                  {r.vendorName}
                </Link>
              </span>
              {r.vendorInvoiceNo ? <span className="tag">No.{r.vendorInvoiceNo}</span> : null}
              <span className="apsubj" title={r.groupName}>{r.groupName}</span>
              <span className="apbal">
                <span className="u">{HOME_MSG.balance}</span> <b className="num">{yen(r.balance)}</b> <span className="u">円</span>
              </span>
              {r.vendorId && !pc.phone ? (
                <button
                  type="button"
                  className="btn aprec"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPaying({ purchaseId: r.purchaseId, vendorId: r.vendorId!, vendorName: r.vendorName });
                  }}
                  data-testid="purchase-due-pay"
                >
                  {VENDOR_PAYMENT_MSG.createShort}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {payable > a.rows.length ? (
        <div className="apmore">
          <Link className="btn text" to="/purchases?tab=unpaid" data-testid="home-purchases-all">
            {PURCHASE_MSG.homeAll(payable)}
          </Link>
        </div>
      ) : null}
      {paying ? <VendorPaymentDialog vendorId={paying.vendorId} vendorName={paying.vendorName} purchaseId={paying.purchaseId} onClose={() => setPaying(null)} /> : null}
    </section>
  );
}
